import { type IEvent } from 'clients/PlatformApi';
import React from 'react';
import useLocale from 'src/hooks/useLocale';
import LocaleUtil from 'utils/LocaleUtil';
import AiFindingsSection, { aiFindingsStyles } from '../AiFindingsSection/AiFindingsSection';

interface SidekickAnswersProps {
  eventColor: string;
  event: IEvent;
  sidekickAnswers: string | undefined;
}

interface Vehicle {
  licensePlateLegibility: string;
  licensePlateReading: string;
  vehicleMake: string;
  vehicleModel: string;
  vehicleColor: string;
  vehicleType: string;
  vehicleDecals: string;
}

export interface EventSpotCheckAnswer {
  title?: string;
  aiChatState: string;
  aiChatSynopsis: string;
  aiChatReasoning: string;
  personsDetected?: number | null;
  customersOfInterestDetected?: number | null;
  employeesOfInterestDetected: number | null;
  bystandersDetected?: number | null;
  relevantCameraNames: string[] | null;
  vehiclesDetected?: boolean | null;
  vehiclesOfInterestDetected?: number | null;
  vehicles?: Vehicle[] | null;
  metadata?: {
    title: string;
  };
}

export const VehicleList: React.FC<{ vehicles: Vehicle[] }> = ({ vehicles }) => {
  const locale = useLocale();

  return (
    <>
      <p>{locale.scheduledAIChat.eventSpotCheckVehicleDescriptions}</p>
      {vehicles.map((vehicle, i) => (
        <ul key={`vehicle-${i}`} className={aiFindingsStyles.vehicleList}>
          <li>
            {LocaleUtil.getLocalizedStr({
              string: locale.scheduledAIChat.eventSpotCheckLicensePlateLegibility,
              replacements: { value: vehicle.licensePlateLegibility },
            })}
          </li>
          <li>
            {LocaleUtil.getLocalizedStr({
              string: locale.scheduledAIChat.eventSpotCheckLicensePlateReading,
              replacements: { value: vehicle.licensePlateReading },
            })}
          </li>
          <li>
            {LocaleUtil.getLocalizedStr({
              string: locale.scheduledAIChat.eventSpotCheckVehicleMake,
              replacements: { value: vehicle.vehicleMake },
            })}
          </li>
          <li>
            {LocaleUtil.getLocalizedStr({
              string: locale.scheduledAIChat.eventSpotCheckVehicleModel,
              replacements: { value: vehicle.vehicleModel },
            })}
          </li>
          <li>
            {LocaleUtil.getLocalizedStr({
              string: locale.scheduledAIChat.eventSpotCheckVehicleColor,
              replacements: { value: vehicle.vehicleColor },
            })}
          </li>
          <li>
            {LocaleUtil.getLocalizedStr({
              string: locale.scheduledAIChat.eventSpotCheckVehicleType,
              replacements: { value: vehicle.vehicleType },
            })}
          </li>
          <li>
            {LocaleUtil.getLocalizedStr({
              string: locale.scheduledAIChat.eventSpotCheckVehicleDecals,
              replacements: { value: vehicle.vehicleDecals },
            })}
          </li>
        </ul>
      ))}
    </>
  );
};

export const FullEventSpotCheckAnswer: React.FC<{
  eventSpotCheckAnswer: EventSpotCheckAnswer;
  displayState?: boolean;
  displaySynopsis?: boolean;
  displayReasoning?: boolean;
  classes?: string | string[];
  dataTestId?: string;
}> = ({
  eventSpotCheckAnswer,
  displayState,
  displaySynopsis,
  displayReasoning,
  classes,
  dataTestId,
}) => {
  const locale = useLocale();
  const {
    aiChatState,
    aiChatReasoning,
    aiChatSynopsis,
    personsDetected,
    customersOfInterestDetected,
    employeesOfInterestDetected,
    bystandersDetected,
    vehiclesDetected,
    relevantCameraNames,
    vehiclesOfInterestDetected,
    vehicles,
  }: EventSpotCheckAnswer = eventSpotCheckAnswer;

  const validEventSpotCheck = !!aiChatState && !!aiChatReasoning && !!aiChatSynopsis;

  if (!validEventSpotCheck) {
    return (
      <pre
        className={typeof classes === 'string' ? classes : classes?.join(' ')}
        data-testid={dataTestId}
        dangerouslySetInnerHTML={{
          __html: eventSpotCheckAnswer,
        }}
      />
    );
  }

  return (
    <div
      className={typeof classes === 'string' ? classes : classes?.join(' ')}
      data-testid={dataTestId}
    >
      {displayState && <p>{aiChatState}</p>}
      {displaySynopsis && <p>{aiChatSynopsis}</p>}
      {displayReasoning && <p>{aiChatReasoning}</p>}
      {Array.isArray(relevantCameraNames) && relevantCameraNames.length > 0 && (
        <p>
          {LocaleUtil.getLocalizedStr({
            string: locale.scheduledAIChat.eventSpotCheckRelevantCameras,
            replacements: { value: relevantCameraNames.join(', ') },
          })}
        </p>
      )}
      <br />
      {typeof personsDetected === 'number' && (
        <p>
          {LocaleUtil.getLocalizedStr({
            string: locale.scheduledAIChat.eventSpotCheckPersonsDetected,
            replacements: { value: personsDetected },
          })}
        </p>
      )}
      {typeof customersOfInterestDetected === 'number' && (
        <p>
          {LocaleUtil.getLocalizedStr({
            string: locale.scheduledAIChat.eventSpotCheckCustomersOfInterestDetected,
            replacements: { value: customersOfInterestDetected },
          })}
        </p>
      )}
      {typeof employeesOfInterestDetected === 'number' && (
        <p>
          {LocaleUtil.getLocalizedStr({
            string: locale.scheduledAIChat.eventSpotCheckEmployeesOfInterestDetected,
            replacements: { value: employeesOfInterestDetected },
          })}
        </p>
      )}
      {typeof bystandersDetected === 'number' && (
        <p>
          {LocaleUtil.getLocalizedStr({
            string: locale.scheduledAIChat.eventSpotCheckBystandersDetected,
            replacements: { value: bystandersDetected },
          })}
        </p>
      )}
      {typeof vehiclesDetected === 'boolean' && (
        <p>
          {LocaleUtil.getLocalizedStr({
            string: locale.scheduledAIChat.eventSpotCheckVehiclesDetected,
            replacements: { value: JSON.stringify(vehiclesDetected) },
          })}
        </p>
      )}
      {typeof vehiclesOfInterestDetected === 'number' && (
        <p>
          {LocaleUtil.getLocalizedStr({
            string: locale.scheduledAIChat.eventSpotCheckVehiclesOfInterestDetected,
            replacements: { value: vehiclesOfInterestDetected },
          })}
        </p>
      )}
      {vehicles?.length && <VehicleList vehicles={vehicles} />}
    </div>
  );
};

const SidekickAnswers: React.FC<SidekickAnswersProps> = (props) => {
  const { eventColor, event, sidekickAnswers } = props;
  const locale = useLocale();

  const activity = event.details?.scheduledActivity;
  const eventSpotCheck = activity?.metadata as any;
  const {
    aiChatState,
    aiChatReasoning,
    aiChatSynopsis,
    relevantCameraNames,
  }: EventSpotCheckAnswer = eventSpotCheck;
  const validEventSpotCheck = !!aiChatState && !!aiChatReasoning && !!aiChatSynopsis;

  const titleText =
    eventSpotCheck?.title ||
    eventSpotCheck?.metadata?.title ||
    activity?.title ||
    locale.scheduledAIChat.eventSpotCheck;

  const summaryContent = sidekickAnswers ? (
    validEventSpotCheck ? (
      <>
        <p>
          {aiChatState}: {aiChatSynopsis}
        </p>
        {Array.isArray(relevantCameraNames) && relevantCameraNames.length > 0 && (
          <p>
            {LocaleUtil.getLocalizedStr({
              string: locale.scheduledAIChat.eventSpotCheckRelevantCameras,
              replacements: { value: relevantCameraNames.join(', ') },
            })}
          </p>
        )}
      </>
    ) : (
      <p>
        {sidekickAnswers.length > 100 ? sidekickAnswers.slice(0, 100) + '...' : sidekickAnswers}
      </p>
    )
  ) : null;

  const expandedContent = sidekickAnswers ? (
    validEventSpotCheck ? (
      <FullEventSpotCheckAnswer
        eventSpotCheckAnswer={eventSpotCheck}
        displayState
        displayReasoning
      />
    ) : (
      <p>{sidekickAnswers}</p>
    )
  ) : null;

  return (
    <AiFindingsSection
      eventColor={eventColor}
      title={titleText}
      tooltip={locale.scheduledAIChat.viewSidekickAnswers}
      summaryContent={summaryContent}
      expandedContent={expandedContent}
    />
  );
};

export default SidekickAnswers;
